/**
 * "Networking" tab — virtual network + subnet (new, isolated by default; or an existing one),
 * public IP (new / existing unassociated / none), NIC network security group
 * (None / Basic / Advanced), fixed "no public inbound ports" and optional load balancing.
 */
import { useState } from 'react'
import { useAzureCatalog } from '@/hooks/useAppriseAzure'
import { RadioField, ReadOnlyField, TextField } from './FormFields'
import type { AppriseLoadBalancerMode, AppriseNsgMode } from '@/types'
import { Banner } from './AppriseFields'
import { LiveSelectField } from './LiveSelect'
import { loadBalancerOfPool } from './appriseState'
import { derivedNames, newVirtualNetworkAllowed, takenVnetNames } from './appriseDefaults'
import styles from './AppriseCreateVm.module.css'
import type { AppriseVmFormApi } from './useAppriseVmForm'

const NEW = '__new__'
const NONE = '__none__'
const key = (rg: string, name: string) => `${rg}/${name}`

function NetworkNote({ form }: { form: AppriseVmFormApi }) {
  const n = form.config.network
  if (n.vnet_mode === 'existing' && !n.vnet_name) return null // nothing chosen yet: the field below asks for a pick
  if (n.vnet_mode === 'create_new') {
    return (
      <Banner role="note">
        <strong>New isolated network.</strong> A new virtual network ({n.vnet_name}) is created for this VM. It is not peered with or
        connected to any other network, so the VM cannot reach other Azure resources or on-premises systems until you add peering, VPN
        or ExpressRoute.
      </Banner>
    )
  }
  return (
    <Banner tone="warning" role="note">
      <strong>Existing network.</strong> The VM joins {n.vnet_name} ({n.vnet_resource_group}) and can reach whatever that network is
      connected to. The network and its subnet are only read — the VM&apos;s network interface is the only thing added to them.
    </Banner>
  )
}

function VirtualNetworkFields({ form }: { form: AppriseVmFormApi }) {
  const n = form.config.network
  const err = form.validation.errors
  const vnets = useAzureCatalog('virtual-networks', { location: form.location }, form.enabled)
  const allowNew = newVirtualNetworkAllowed(form.options)
  const newName = derivedNames(form.config.server.server_name, form.region, takenVnetNames(vnets.items, form.config.server.resource_group_name)).vnet_name
  // nothing chosen yet (existing networks only): the box shows its placeholder instead of an empty "(group)" entry
  const current = n.vnet_mode === 'create_new' ? NEW : n.vnet_name ? key(n.vnet_resource_group, n.vnet_name) : ''
  const selected = vnets.items.find((v) => key(v.resource_group, v.name) === current)
  return (
    <>
      <LiveSelectField label="Virtual network" required value={current} catalog={vnets} placeholder="Choose an existing virtual network…"
        error={err['network.vnet_name'] ?? err['network.vnet_mode']}
        hint={allowNew ? undefined : 'Pick one of the existing virtual networks in this region, then one of its subnets. New networks are not created from here.'}
        emptyText="No virtual networks found in this region"
        valueLabel={n.vnet_mode === 'create_new' ? `(New) ${newName}` : `${n.vnet_name} (${n.vnet_resource_group})`}
        options={[
          ...(allowNew ? [{ value: NEW, label: `(New) ${newName}` }] : []),
          ...vnets.items.map((v) => ({ value: key(v.resource_group, v.name), label: `${v.name} (${v.resource_group}) — ${v.address_prefixes.join(', ') || 'no address space'}` })),
        ]}
        onChange={(v) => {
          const hit = vnets.items.find((x) => key(x.resource_group, x.name) === v)
          if (!hit) { if (allowNew) form.network.useNew(); return }
          form.network.useExisting(hit.name, hit.resource_group, hit.subnets)
        }} />
      {n.vnet_mode === 'create_new' ? (
        <>
          <TextField label="Virtual network name" required mono value={n.vnet_name} error={err['network.vnet_name']} onChange={(v) => form.setNetwork({ vnet_name: v })} />
          <TextField label="Address space" required mono value={n.vnet_address_space} error={err['network.vnet_address_space']} onChange={(v) => form.setNetwork({ vnet_address_space: v })} />
          <TextField label="Subnet name" required mono value={n.subnet_name} error={err['network.subnet_name']} onChange={(v) => form.setNetwork({ subnet_name: v })} />
          <TextField label="Subnet address range" required mono value={n.subnet_prefix} error={err['network.subnet_prefix']} onChange={(v) => form.setNetwork({ subnet_prefix: v })} />
        </>
      ) : (
        <LiveSelectField label="Subnet" required value={n.subnet_name} error={err['network.subnet_name']} valueLabel={n.subnet_name}
          disabled={!n.vnet_name} placeholder={n.vnet_name ? 'Choose a subnet…' : 'Choose a virtual network first'}
          emptyText={n.vnet_name ? 'This virtual network has no subnets' : undefined}
          options={(selected?.subnets ?? []).map((s) => ({ value: s.name, label: `${s.name} (${s.prefix})` }))}
          onChange={(v) => form.network.chooseSubnet(v)} />
      )}
      <NetworkNote form={form} />
    </>
  )
}

function PublicIpFields({ form }: { form: AppriseVmFormApi }) {
  const pip = form.config.network.public_ip
  const err = form.validation.errors
  const ips = useAzureCatalog('public-ips', { location: form.location }, form.enabled)
  const newName = derivedNames(form.config.server.server_name, form.region).public_ip_name
  const current = pip.mode === 'create_new' ? NEW : pip.mode === 'none' ? NONE : key(pip.resource_group, pip.name)
  return (
    <>
      <LiveSelectField label="Public IP" value={current} catalog={ips} error={err['network.public_ip.name'] ?? err['network.public_ip.resource_group']}
        valueLabel={pip.mode === 'create_new' ? `(New) ${newName}` : pip.mode === 'none' ? 'None' : `${pip.name} (${pip.resource_group})`}
        warning={form.validation.warnings['network.public_ip.mode']}
        options={[
          { value: NEW, label: `(New) ${newName}` },
          { value: NONE, label: 'None' },
          ...ips.items.map((i) => ({ value: key(i.resource_group, i.name), label: `${i.name} (${i.resource_group}) — ${i.sku} · ${i.allocation}` })),
        ]}
        hint="Only public IPs that are not attached to anything are offered"
        onChange={(v) => {
          if (v === NEW) form.network.publicIp('create_new')
          else if (v === NONE) form.network.publicIp('none')
          else {
            const hit = ips.items.find((i) => key(i.resource_group, i.name) === v)
            if (hit) form.network.publicIp('existing', { name: hit.name, resource_group: hit.resource_group })
          }
        }} />
      {pip.mode === 'create_new' ? (
        <TextField label="Public IP name" required mono value={pip.name} error={err['network.public_ip.name']} hint="Standard SKU, static"
          onChange={(v) => form.setPublicIp({ name: v })} />
      ) : null}
    </>
  )
}

function NsgFields({ form }: { form: AppriseVmFormApi }) {
  const n = form.config.network
  const err = form.validation.errors
  const nsgs = useAzureCatalog('network-security-groups', { location: form.location }, form.enabled && n.nsg_mode === 'existing')
  return (
    <>
      <RadioField<AppriseNsgMode> name="ap_nsg_mode" label="NIC network security group" value={n.nsg_mode} full error={err['network.nsg_mode']}
        options={[{ value: 'none', label: 'None' }, { value: 'create_new', label: 'Basic' }, { value: 'existing', label: 'Advanced' }]}
        hint={n.nsg_mode === 'create_new' ? `Basic: a new network security group (${n.nsg_name}) is created with no inbound rules.`
          : n.nsg_mode === 'existing' ? 'Advanced: attach an existing network security group (it is never modified).'
          : 'No network security group is attached to the network interface.'}
        onChange={(v) => form.network.nsg(v)} />
      {n.nsg_mode === 'existing' && (
        <LiveSelectField label="Configure network security group" required full catalog={nsgs} value={n.nsg_name ? key(n.nsg_resource_group, n.nsg_name) : ''}
          error={err['network.nsg_name'] ?? err['network.nsg_resource_group']} emptyText="No network security groups found in this region"
          valueLabel={n.nsg_name ? `${n.nsg_name} (${n.nsg_resource_group})` : undefined}
          placeholder="Choose one…" options={nsgs.items.map((g) => ({ value: key(g.resource_group, g.name), label: `${g.name} (${g.resource_group})` }))}
          onChange={(v) => { const hit = nsgs.items.find((g) => key(g.resource_group, g.name) === v); if (hit) form.network.nsg('existing', hit) }} />
      )}
    </>
  )
}

function LoadBalancingFields({ form }: { form: AppriseVmFormApi }) {
  const lb = form.config.network.load_balancer
  const err = form.validation.errors
  const lbs = useAzureCatalog('load-balancers', { location: form.location }, form.enabled && lb.mode === 'existing')
  const [pickedLb, setPickedLb] = useState('')
  const owner = loadBalancerOfPool(lbs.items, lb.backend_pool_id)
  const lbKey = owner ? key(owner.resource_group, owner.name) : pickedLb
  const selected = lbs.items.find((x) => key(x.resource_group, x.name) === lbKey)
  return (
    <>
      <RadioField<AppriseLoadBalancerMode> name="ap_lb_mode" label="Load balancing" value={lb.mode} full
        options={[{ value: 'none', label: 'None' }, { value: 'existing', label: 'Existing load balancer' }]}
        onChange={(v) => { setPickedLb(''); form.setLoadBalancer(v === 'none' ? { mode: 'none', backend_pool_id: '' } : { mode: 'existing' }) }} />
      {lb.mode === 'existing' && (
        <>
          <LiveSelectField label="Load balancer" required catalog={lbs} value={lbKey} emptyText="No load balancers found in this region"
            placeholder="Choose one…" options={lbs.items.map((x) => ({ value: key(x.resource_group, x.name), label: `${x.name} (${x.resource_group}) — ${x.sku}` }))}
            onChange={(v) => { setPickedLb(v); form.setLoadBalancer({ backend_pool_id: '' }) }} />
          <LiveSelectField label="Backend pool" required value={lb.backend_pool_id} error={err['network.load_balancer.backend_pool_id']}
            emptyText={lbKey ? 'This load balancer has no backend pools' : 'Choose a load balancer first'} disabled={!selected}
            valueLabel={lb.backend_pool_id.split('/').pop()}
            placeholder="Choose one…" options={(selected?.backend_pools ?? []).map((p) => ({ value: p.id, label: p.name }))}
            onChange={(v) => form.network.loadBalancer(v)} />
        </>
      )}
    </>
  )
}

export function AppriseNetworkingTab({ form }: { form: AppriseVmFormApi }) {
  return (
    <>
      <div className={styles.sectionTitle}>Network interface</div>
      <VirtualNetworkFields form={form} />
      <PublicIpFields form={form} />
      <NsgFields form={form} />
      <ReadOnlyField label="Public inbound ports" value="None" hint="Fixed — no inbound port is opened" />
      <ReadOnlyField label="Delete public IP and NIC when VM is deleted" value="Yes" hint="Fixed" />
      <div className={styles.sectionTitle}>Load balancing</div>
      <LoadBalancingFields form={form} />
    </>
  )
}
